import { message } from "antd";
import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import usePostData from "../../api/usePostData";
import AppInput from "../../common/AppInput";
import Button from "../../components/Button/Button";
import Recaptcha from "../../components/Recaptcha/Recaptcha";
import { registerEmailValidation } from "../../utils/validations";
import { RegisterTracking } from "../../utils/mixpanel";
import PropTypes from "prop-types";
/**
 * A component that represents the email step form of the registeration process.
 * It is stored in a separate component because there are two different places where the registeration takes place with some differences in each.
 * It is intended for this component to not call the useContext hook directly, but for its parent to do so instead.
 *
 * @param {Object} props
 * @param {{email: string}} props.initialValues - The initial values of the form, only the email
 * @param {(email: string)=>void} props.onSubmitSuccess - A callback function to be called when the form submission is successful
 * @returns {JSX.Element}
 */

const EmailStepForm = ({ initialValues, onSubmitSuccess, setExistEmail, setTempEmail }) => {
  const { t } = useTranslation("global");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const formikRef = useRef(null);
  const navigate = useNavigate();
  const { postData, loading: sendingRequest, errors } = usePostData();
  const onSubmit = async (values, { setSubmitting }) => {
    if (recaptchaToken == null) {
      return message.error(t("recaptchaRequired"));
    }
    // Make API call to register user
    const data = {
      email: values.email,
    };
    const checkEmailResponse = await postData({
      route: "api/method/airport_transport.api.user.check_email",
      data,
      params: {
        headers: {
          recaptchaToken,
        },
      },
    });
    if (checkEmailResponse.status === 200) {
      const otpResponse = await postData({
        route: "api/method/airport_transport.api.user.send_confirmation_email",
        data: { email: values.email },
        params: {
          headers: {
            recaptchaToken,
          },
        },
      });
      // Redirect to OTP verification screen
      if (otpResponse?.status === 200) {
        const msg = otpResponse?.data?.msg?.toLowerCase();
        if (msg === "otp sent") {
          message.success(t("otp_sent"));
        } else {
          message.success(`${msg}`);
        }
        onSubmitSuccess(values.email);
      }
    }
    setSubmitting(false);
  };
  useEffect(() => {
    if (errors) {
      const errorMsg = errors?.error?.toLowerCase();
      if (errorMsg === "user already exists") {
        message.error(t("errors.user_already_exists"));
        RegisterTracking(false, errorMsg);
        if(setExistEmail){
         setExistEmail(true);
        }

      } else {
        message.error(`${errorMsg}`);
      }
      recaptchaRef?.current.reset();
      setRecaptchaToken(null);
    }
  }, [errors]);

  useEffect(() => {
    if(formikRef?.current?.values?.email && setTempEmail){
      setTempEmail(formikRef?.current?.values?.email);
    }
  }, [formikRef?.current?.values?.email]);
  
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={registerEmailValidation}
      onSubmit={onSubmit}
      innerRef={formikRef}
    >
      {({ errors, touched }) => (
        <Form>
          <AppInput
            name="email"
            type="email"
            label={t("hero.enter_email_text")}
            error={errors.email}
            touched={touched.email}
          />
          <div>
            <Recaptcha
              recaptchaRef={recaptchaRef}
              onChange={(value) => {
                setRecaptchaToken(value);
              }}
            />
          </div>
          <div className="text-center mt-6">
            <Button
              className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium rounded text-sm px-5 py-2.5 me-2 mb-2"
              label={t("create_a_text")}
              type="submit"
              disabled={sendingRequest}
            />
          </div>

          <div className="mt-2 w-full text-sm flex items-center justify-center text-text_lightdark_grey cursor-pointer">
            {t("already_account_text")}
             <button
                className="text-sm font-semibold ms-2 text-text_steel_blue hover:underline bg-transparent border-0 p-0 m-0"
                onClick={() => {
                  if (setExistEmail) {
                    setExistEmail(true);
                  } else {
                    navigate('/sign-in');
                  }
                }}
              >
                {t("header.sign_in_text")}
              </button>

          </div>
        </Form>
      )}
    </Formik>
  );
};
EmailStepForm.propTypes = {
  initialValues: PropTypes.shape({
    email: PropTypes.string.isRequired,
  }).isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
  setExistEmail: PropTypes.func,
  setTempEmail: PropTypes.func,
};
export default EmailStepForm;
